import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import lexofficeService from '@/services/lexoffice.service';
import { useTranslation } from 'react-i18next';

const LexofficeSection: React.FC = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: cfg, isLoading } = useQuery({ queryKey: ['lexoffice-config'], queryFn: () => lexofficeService.getConfig() });
  const [apiKey, setApiKey] = useState('');
  const [dirty, setDirty] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => { if (cfg) { setApiKey(cfg.api_key === '[HIDDEN]' ? '' : (cfg.api_key || '')); setDirty(false); } }, [cfg]);

  const saveM = useMutation({
    mutationFn: () => lexofficeService.saveConfig(apiKey),
    onSuccess: () => { toast.success(t('common:x3.lexofficeSaved', 'Lexoffice gespeichert')); setDirty(false); qc.invalidateQueries({ queryKey: ['lexoffice-config'] }); },
    onError: () => toast.error(t('common:state.saveFailed', 'Speichern fehlgeschlagen')),
  });
  const testM = useMutation({
    mutationFn: () => lexofficeService.test(),
    onSuccess: (r) => { r.success ? toast.success(`Lexoffice verbunden ✓ ${r.message || ''}`) : toast.error(`Test fehlgeschlagen: ${r.message || ''}`); qc.invalidateQueries({ queryKey: ['lexoffice-config'] }); },
    onError: () => toast.error(t('common:state.testFailed', 'Test fehlgeschlagen')),
  });

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0086b3]/10 text-[#0086b3]"><FileText className="h-5 w-5" /></div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-slate-800">{t('common:x3.lexofficeInvoices', 'Lexoffice — Rechnungen')}</h2>
            {cfg?.last_test_status === 'success' && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-600"><CheckCircle2 className="h-3 w-3" /> {t('common:x1.connected', 'Verbunden')}</span>}
            {cfg?.last_test_status === 'failed' && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-600"><XCircle className="h-3 w-3" /> {t('common:state.error', 'Fehler')}</span>}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">API-Key (lexoffice → Einstellungen → Öffentliche API). Rechnungen werden per Kunde &amp; Bestellnummer gefunden und im Ticket angezeigt.</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-16 items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs text-slate-500">{t('common:x3.apiKeyLabel', 'API-Key')}</Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setDirty(true); }}
              placeholder={cfg?.api_key === '[HIDDEN]' ? '•••••••• gespeichert' : ''}
              className="mt-1 h-9 font-mono text-xs"
              autoComplete="new-password" name="lex_api_key" spellCheck={false}
              data-lpignore="true" data-1p-ignore="true"
              readOnly={!unlocked} onFocus={() => setUnlocked(true)}
            />
          </div>
          <Button onClick={() => saveM.mutate()} disabled={saveM.isPending || !dirty} className="h-9 bg-[#0f172a] text-white hover:bg-slate-800">
            {saveM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
          </Button>
          <Button variant="outline" onClick={() => testM.mutate()} disabled={testM.isPending || !cfg?.connected} className="h-9">
            {testM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Testen'}
          </Button>
        </div>
      )}
      {cfg?.last_test_status === 'failed' && cfg?.last_test_error && <p className="mt-2 text-xs text-rose-600">{cfg.last_test_error}</p>}
    </div>
  );
};

export default LexofficeSection;
